/**
 * The lines that stand beside the notes rather than on them, as `<direction>` elements.
 *
 * A hairpin, a pedal and an octave sign are one thing in the document -- a spanner joining
 * two events -- and three different elements in the format, each pairing its own ends by its
 * own numbers. What they share is where they go: the start is written immediately before the
 * note it opens on and the stop immediately after the note it closes on, so that a line drawn
 * over a single long note does not end before it begins.
 *
 * An octave sign names what is *printed*, not what is played. `<octave-shift type="down">`
 * says the notes were moved down the page by an octave, which is what an `8va` written above
 * the staff does: the pitches stay where they are and the sign puts them back. So the export
 * writes the document's pitches unchanged under an octave line, and the renderer moves them.
 */

import { isOctaveShift } from "../document/types";
import type { OctaveShift } from "../document/types";
import { el } from "../xml/write";
import type { XmlNode } from "../xml/write";
import type { HeldMark } from "./export-plan";

/** Which way the printed notes were moved, and by how much, for each of the four signs. */
const OCTAVE_SIGNS: Readonly<Record<OctaveShift, { direction: "up" | "down"; size: number }>> = {
  "8va": { direction: "down", size: 8 },
  "8vb": { direction: "up", size: 8 },
  "15ma": { direction: "down", size: 15 },
  "15mb": { direction: "up", size: 15 },
};

/** The element that draws one held line, at whichever of its two ends this is. */
function heldNode(mark: HeldMark, end: "start" | "stop"): XmlNode {
  if (mark.kind === "pedal") {
    // `line="yes"` asks for the bracket rather than the `Ped.`/`*` pair. The bracket shows
    // how long the pedal is held, which is the only reason the document holds it as a line.
    return el("pedal", { type: end, line: "yes" });
  }
  if (isOctaveShift(mark.kind)) {
    const sign = OCTAVE_SIGNS[mark.kind];
    return el("octave-shift", {
      type: end === "start" ? sign.direction : "stop",
      size: sign.size,
      number: mark.number,
    });
  }
  return el("wedge", { type: end === "start" ? mark.kind : "stop", number: mark.number });
}

/**
 * One end of a held line, as the `<direction>` it is written in.
 *
 * Only a start states where the line sits. A stop that repeated the placement would be read
 * as a second, contradictory instruction about a line already being drawn.
 */
export function heldDirection(mark: HeldMark, end: "start" | "stop"): XmlNode {
  return el("direction", { placement: end === "start" ? mark.placement : null }, [
    el("direction-type", {}, [heldNode(mark, end)]),
  ]);
}
